import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { CartProvider } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import TelegramButton from "@/components/TelegramButton";
import AnnouncementBar from "@/components/AnnouncementBar";
import ShippingMarquee from "@/components/ShippingMarquee";
import AgeGate from "@/components/AgeGate";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { trackPageView } from "@/lib/analytics";
import { useReferralCapture } from "@/hooks/useReferralCapture";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Checkout from "@/pages/Checkout";
import Research from "@/pages/Research";
import BPC157Research from "@/pages/research/BPC157Research";
import ReceptorSignaling from "@/pages/research/ReceptorSignaling";
import HandlingReconstitution from "@/pages/research/HandlingReconstitution";
import AdminReferrals from "@/pages/AdminReferrals";

const RouteFx = () => {
    const location = useLocation();
    useScrollReveal();
    useReferralCapture();
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        // Defer one tick so document.title (set by each page) is up to date
        // before GA4 reads it for the page_view event.
        const id = window.setTimeout(() => {
            trackPageView(location.pathname);
        }, 0);
        return () => window.clearTimeout(id);
    }, [location.pathname, location.search]);
    return null;
};

/** Public site chrome (marquee, navbar, footer, cart, telegram) — hidden on /admin routes. */
const PublicChrome = ({ children }) => {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    if (isAdmin) {
        return <main>{children}</main>;
    }
    return (
        <>
            <AgeGate />
            <ShippingMarquee />
            <AnnouncementBar />
            <Navbar />
            <main>{children}</main>
            <Footer />
            <CartSheet />
            <TelegramButton />
        </>
    );
};

function App() {
    return (
        <div className="App min-h-screen bg-[#05050A] text-slate-100">
            <BrowserRouter>
                <CartProvider>
                    <RouteFx />
                    <PublicChrome>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/products" element={<Products />} />
                            <Route path="/research" element={<Research />} />
                            <Route path="/research/bpc-157-research" element={<BPC157Research />} />
                            <Route path="/research/receptor-signaling" element={<ReceptorSignaling />} />
                            <Route path="/research/handling-reconstitution" element={<HandlingReconstitution />} />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/admin/referrals" element={<AdminReferrals />} />
                            <Route path="*" element={<Home />} />
                        </Routes>
                    </PublicChrome>
                    <Toaster
                        theme="dark"
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                background: "#0A0A12",
                                border: "1px solid rgba(255,255,255,0.1)",
                                color: "#F8FAFC",
                            },
                        }}
                    />
                </CartProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
